/**
 * AccountDashboard.js — GoHost Staff Login Form
 *
 * Renders a login form. On success, calls onLogin(user) — the parent
 * decides what to do next (AccountModal navigates to /portal).
 *
 * Also handles the forgot-password flow inline:
 *   'login'        — standard username + password form
 *   'forgot-email' — enter email to receive a reset code
 *   'forgot-code'  — enter code + new password to complete the reset
 */

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { signInAnonymously } from 'firebase/auth';
import { app, auth } from '../firebase';

// Must match SESSION_EXPIRED_KEY in AccountModal.js — inlined here to avoid circular import
const SESSION_EXPIRED_KEY = 'gohost_session_expired';

const functions = getFunctions(app);
const verifyGoHostLogin     = httpsCallable(functions, 'verifyGoHostLogin');
const forgotPortalPassword  = httpsCallable(functions, 'forgotPortalPassword');
const confirmPasswordReset  = httpsCallable(functions, 'confirmPasswordReset');

// ─── Login view ───────────────────────────────────────────────────────────────

const LoginForm = ({ onLogin, onForgot, sessionExpired }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) return;

    setLoading(true);
    setError('');

    try {
      // Ensure there is a Firebase Auth user before calling verifyGoHostLogin.
      // firebase.js signs in anonymously on init, but that call is async and may
      // not have resolved yet if the user submits the form quickly. Without a uid,
      // verifyGoHostLogin skips setCustomUserClaims entirely, and every subsequent
      // call to updatePortalAccount (and other claim-gated functions) returns 403.
      let firebaseUid = auth.currentUser?.uid || null;
      if (!firebaseUid) {
        const anonResult = await signInAnonymously(auth);
        firebaseUid = anonResult.user.uid;
      }

      const result = await verifyGoHostLogin({ username: username.trim(), password, firebaseUid });
      // Force token refresh so the new custom claims (accountId, restaurantIds, role)
      // are included in the client's ID token before the portal page loads.
      await auth.currentUser.getIdToken(true);
      onLogin({ user: result.data.user, remember });
    } catch (err) {
      const code = err?.code || '';
      if (code === 'functions/unauthenticated') {
        setError('Invalid username or password.');
      } else if (code === 'functions/permission-denied') {
        setError('This account has been deactivated. Contact your administrator.');
      } else if (code === 'functions/invalid-argument') {
        setError('Please enter your username and password.');
      } else if (code === 'functions/unavailable') {
        setError('Login service is temporarily unavailable. Please try again shortly.');
      } else {
        setError('Something went wrong. Please try again in a moment.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Username</label>
        <input
          type="text"
          placeholder="Enter your username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:opacity-50"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Password</label>
        <input
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:opacity-50"
          disabled={loading}
        />
      </div>
      {/* Remember me */}
      <label className="flex items-center gap-2.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={remember}
          onChange={e => setRemember(e.target.checked)}
          className="w-4 h-4 rounded border-neutral-300 accent-brand-blue cursor-pointer"
        />
        <span className="text-xs text-neutral-500">Remember me on this device</span>
      </label>

      {sessionExpired && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Your session expired. Please sign in again.
        </p>
      )}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !username.trim() || !password}
        className="w-full py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-xl hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors mt-1"
      >
        {loading ? 'Signing in…' : 'Sign In'}
      </button>
      <button
        type="button"
        onClick={onForgot}
        className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors text-center mt-1"
      >
        Forgot password?
      </button>
    </form>
  );
};

// ─── Forgot password — step 1: enter email ───────────────────────────────────

const ForgotEmailForm = ({ onCodeSent, onBack }) => {
  const [email, setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');

    try {
      const result = await forgotPortalPassword({ email: email.trim() });
      // Pass the email and masked destination to the next step
      onCodeSent({ email: email.trim(), maskedEmail: result.data.maskedEmail || null });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div>
        <p className="text-xs text-neutral-500 mb-3 leading-relaxed">
          Enter the email address on your account. If it matches, we'll send you a reset code.
        </p>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Email address</label>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:opacity-50"
          disabled={loading}
        />
      </div>
      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !email.trim()}
        className="w-full py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-xl hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Sending…' : 'Send Reset Code'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors text-center"
      >
        Back to sign in
      </button>
    </form>
  );
};

// ─── Forgot password — step 2: enter code + new password ─────────────────────

const ForgotCodeForm = ({ email, maskedEmail, onSuccess, onBack }) => {
  const [code, setCode]         = useState('');
  const [newPw, setNewPw]       = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !newPw || !confirmPw) return;

    if (newPw !== confirmPw) {
      setError('Passwords do not match.');
      return;
    }
    if (newPw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await confirmPasswordReset({ email, code: code.trim(), newPassword: newPw });
      onSuccess();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <p className="text-xs text-neutral-500 leading-relaxed">
        {maskedEmail
          ? <>A reset code was sent to <span className="font-medium text-neutral-700">{maskedEmail}</span>. Enter it below along with your new password.</>
          : 'If that email is on file, a reset code was sent. Enter it below along with your new password.'
        }
      </p>
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Reset code</label>
        <input
          type="text"
          inputMode="numeric"
          placeholder="6-digit code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue tracking-widest text-center disabled:opacity-50"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">New password</label>
        <input
          type="password"
          placeholder="At least 8 characters"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          autoComplete="new-password"
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:opacity-50"
          disabled={loading}
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-neutral-500 mb-1">Confirm new password</label>
        <input
          type="password"
          placeholder="Repeat new password"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          autoComplete="new-password"
          className="w-full px-3 py-2.5 text-sm text-neutral-800 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue disabled:opacity-50"
          disabled={loading}
        />
      </div>
      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
      )}
      <button
        type="submit"
        disabled={loading || !code.trim() || !newPw || !confirmPw}
        className="w-full py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-xl hover:bg-brand-blue-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Resetting…' : 'Reset Password'}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors text-center"
      >
        Back to sign in
      </button>
    </form>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const AccountDashboard = ({ onLogin }) => {
  // view: 'login' | 'forgot-email' | 'forgot-code' | 'reset-success'
  const [view, setView]               = useState('login');
  const [resetEmail, setResetEmail]   = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  // Check if we were auto-redirected here due to a session expiry
  const [sessionExpired] = useState(() => {
    const was = !!localStorage.getItem(SESSION_EXPIRED_KEY);
    if (was) localStorage.removeItem(SESSION_EXPIRED_KEY);
    return was;
  });

  if (view === 'forgot-email') {
    return (
      <ForgotEmailForm
        onCodeSent={({ email, maskedEmail: masked }) => {
          setResetEmail(email);
          setMaskedEmail(masked || '');
          setView('forgot-code');
        }}
        onBack={() => setView('login')}
      />
    );
  }

  if (view === 'forgot-code') {
    return (
      <ForgotCodeForm
        email={resetEmail}
        maskedEmail={maskedEmail}
        onSuccess={() => setView('reset-success')}
        onBack={() => setView('login')}
      />
    );
  }

  if (view === 'reset-success') {
    return (
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="w-12 h-12 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-600 text-xl font-bold">
          ✓
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-neutral-800">Password reset</p>
          <p className="text-xs text-neutral-500 mt-1">Your password has been updated. You can now sign in.</p>
        </div>
        <button
          onClick={() => setView('login')}
          className="w-full py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-xl hover:bg-brand-blue-hover transition-colors"
        >
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <LoginForm
      onLogin={onLogin}
      onForgot={() => setView('forgot-email')}
      sessionExpired={sessionExpired}
    />
  );
};

export default AccountDashboard;
